import {IDeepLink, IGroupTypeSyncMode, IHelpMenuItem, ISyncSource} from '@app/models';

export enum AppViewType {
    addAppListView = 0,
    addCatalogApp = 1,
    addWebWorkflowApp = 2,
    addCustomApp = 3
}

export enum UserReportsType {
    userSee = 'userSee',
    userSeen = 'userSeen',
    userStillKnow = 'userStillKnow',
    userBeenDoingLately = 'userBeenDoingLately'
}

export enum PasswordReportsType {
    aboutToExpire = 'aboutToExpire',
    outOfSync = 'outOfSync',
    notBeingSynced = 'notBeingSynced',
    accessed = 'accessed',
    overridingVaultPolicy = 'overridingVaultPolicy'
}

export enum VaultReportsType {
    createdRecently = 'createdRecently',
    changedRecently = 'changedRecently',
    exportedRecently = 'exportedRecently',
    grantedPermissions = 'grantedPermissions'
}

export enum AppStageType {
    addApp = 0,
    editApp = 1
}

export enum APP_ICON_DIMENSIONS {
    width = 64,
    height = 64
}

export enum APP_TYPES {
    catalog = '#Kaseya.AuthAnvil.Models.CatalogApplication',
    workflow = '#Kaseya.AuthAnvil.Models.WorkflowApplication',
    catalogWorkflow = '#Kaseya.AuthAnvil.Models.CatalogWorkflowApplication',
    application = 'Kaseya.AuthAnvil.Models.Application'
}

export enum KEY_CODES {
    enter = 13,
    escape = 27
}

export enum suppliedCredentialTypes {
    user = 'user',
    admin = 'administrator'
}

export const SUPPLIED_CREDENTIAL = suppliedCredentialTypes.user;

export const SUPPLIED_CREDENTIALS_OPTIONS = [
    { label: 'USER_SUPPLIED', value: suppliedCredentialTypes.user },
    { label: 'ADMINISTRATOR_SUPPLIED', value: suppliedCredentialTypes.admin }
];

// PROTOCOL TAB
export enum PROTOCOL_TYPES_VALUE {
    spInit = 'SpInit',
    idpInit = 'IdpInit',
    wsFed = 'WSFed',
    oAuth2 = 'OAuth2'
}

export enum PROTOCOL_VERSIONS_VALUE {
    WsOnePointThree = 'WS 1.3',
    WsTwoKFive = 'WS 2005'
}

export const PROTOCOL_VERSIONS = [
    { label: PROTOCOL_VERSIONS_VALUE.WsOnePointThree, value: PROTOCOL_VERSIONS_VALUE.WsOnePointThree },
    { label: PROTOCOL_VERSIONS_VALUE.WsTwoKFive, value: PROTOCOL_VERSIONS_VALUE.WsTwoKFive }
];

export const PROTOCOL_TYPES = (translation: (val: string) => string) => [
    { label: translation('PROTOCOL_TYPE_SAML_SP_INIT'), value: PROTOCOL_TYPES_VALUE.spInit },
    { label: translation('PROTOCOL_TYPE_SAML_IDP_INIT'), value: PROTOCOL_TYPES_VALUE.idpInit },
    { label: translation('PROTOCOL_TYPE_WS_FEDERATION'), value: PROTOCOL_TYPES_VALUE.wsFed }
];
export const PROTOCOL_TYPES_OFFICE365 = (translation: (val: string) => string) => [
    { label: translation('PROTOCOL_TYPE_WS_FEDERATION'), value: PROTOCOL_TYPES_VALUE.wsFed }
];

export interface ISigninAlgoValue {
    signature: string;
    digest: string;
}

export interface ISigninAlgo {
    label: string;
    value: ISigninAlgoValue;
}

export const SIGNIN_ALGOS = [
    { label: 'SHA-1', value: { signature: 'http://www.w3.org/2000/09/xmldsig#rsa-sha1', digest: 'http://www.w3.org/2000/09/xmldsig#sha1' } },
    { label: 'SHA-256', value: { signature: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256', digest: 'http://www.w3.org/2001/04/xmlenc#sha256' } }
];

export const TOKEN_LIFETIME = 60;

export const ATTRIBUTE_ISSUANCE_SELECTION = 'username';

export const CUSTOM_ATTRIBUTE_MAPS = [];

export const NEW_AUDIENCE_URI = '';

export const SIGNING_CERT = '';
export const ENCRYPTION_CERT = '';

// SEED GENERATION

export const AUDIENCE_TEMPLATE = (AUDIENCE_URI: string) => {
    return `Audiences = new[] {
        new AudienceUri {
            Tenant = systemTenant,
            Organization = defaultOrg,
            Uri = "${AUDIENCE_URI}"
        }
    },`;
};

export const SEED_TEMPALTE = (
    APP_NAME: string,
    APP_SERVICE_ISSUER: string,
    APP_DESCRIPTION: string,
    APP_PROTOCOL: string,
    APP_PROTOCOL_LIST: string,
    APP_REPLY_TO: string,
    APP_ICON_URL: string,
    APP_SERVICE_CONFIG: string,
    APP_ATTRIBUTE_RULES: string,
    AUDIENCE_URI?: string
) => {
    return `"new CatalogApplication
    {
        Tenant = systemTenant,
        TenantId = Guid.Empty,
        Enabled = false,
        IsSystemApplication = false,
        SupportsConsent = false,
        SupportsRefreshToken = false,
        SigningKey = new RsaSecretKey
        {
            CommonName = "${APP_NAME} Signing Key",
            KeyGeneration = KeyGenerationOptions.Certificate,
            WrapKey = tenantKey
        },
        ${AUDIENCE_URI ? AUDIENCE_TEMPLATE(AUDIENCE_URI) : ''}
        }
        Policy = defaultOrg.DefaultPolicy,
        IdentityIssuerUri = $"https://{systemTenant.HomeRealm}.my.passly.com/trust",
        ServiceIssuerUri = ${APP_SERVICE_ISSUER},
        DisplayName = "${APP_NAME}",
        Description = "${APP_DESCRIPTION}",
        AccessTokenLifetime = TimeSpan.FromMinutes(480),
        RefreshTokenLifetime = TimeSpan.FromHours(0),
        Protocol = Protocol.${APP_PROTOCOL},
        Protocols = "${APP_PROTOCOL_LIST}",
        ReplyToUrl = "${APP_REPLY_TO}",
        IconUrl = "${APP_ICON_URL}",
        ServiceConfiguration = "${APP_SERVICE_CONFIG}",
        AttributeRules = "${APP_ATTRIBUTE_RULES}"
    }`;
};

export interface IWorkflowStep {
    step: string;
    type: string;
    getelementby: string;
    elementval: string;
    value: string | null;
    getframeby?: string;
    frameval?: string;
    formval?: string;
    getformby?: string;
}

// ATTRIBUTE TRANSFORMATION
export const KNOWN_ATTRIBUTE_TYPES = [
    'actort',
    'birthdate',
    'email',
    'family_name',
    'gender',
    'given_name',
    'nameid',
    'sub',
    'website',
    'unique_name',
    'oid',
    'scp',
    'tid',
    'acr',
    'adfs1email',
    'adfs1upn',
    'amr',
    'auth_time',
    'authmethod',
    'group',
    'groupsid',
    'idp',
    'ppid',
    'primarygroupsid',
    'primarysid',
    'proxy',
    'pwdchgurl',
    'pwdexpdays',
    'pwdexptime',
    'role',
    'roles',
    'upn',
    'winaccountname',
    'http://schemas.xmlsoap.org/ws/2009/09/identity/claims/actor',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/dateofbirth',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/gender',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/webpage',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
    'http://schemas.microsoft.com/identity/claims/objectidentifier',
    'http://schemas.microsoft.com/identity/claims/scope',
    'http://schemas.microsoft.com/identity/claims/tenantid',
    'http://schemas.microsoft.com/claims/authnclassreference',
    'http://schemas.xmlsoap.org/claims/EmailAddress',
    'http://schemas.xmlsoap.org/claims/UPN',
    'http://schemas.microsoft.com/claims/authnmethodsreferences',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/authenticationinstant',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/authenticationmethod',
    'http://schemas.xmlsoap.org/claims/CommonName',
    'http://schemas.xmlsoap.org/claims/Group',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/groupsid',
    'http://schemas.microsoft.com/identity/claims/identityprovider',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/privatepersonalidentifier',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/primarygroupsid',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/primarysid',
    'http://schemas.microsoft.com/ws/2012/01/passwordchangeurl',
    'http://schemas.microsoft.com/ws/2012/01/passwordexpirationtime',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/windowsaccountname'
];

export const NAME_IDENTIFIER_ATTRIBUTE = KNOWN_ATTRIBUTE_TYPES[39];

export const CLAIM_FORMAT_TYPES = [
    'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
    'urn:oasis:names:tc:SAML:2.0:nameid-format:entity',
    'urn:oasis:names:tc:SAML:2.0:nameid-format:kerberos',
    'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
    'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
    'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
    'urn:oasis:names:tc:SAML:1.1:nameid-format:WindowsDomainQualifiedName',
    'urn:oasis:names:tc:SAML:1.1:nameid-format:X509SubjectName'
];

export enum ATTRIBUTE_ISSUERS {
    username = 'username',
    custom = 'custom'
}

export const availableAttributes = ['{User.PrincipalName}', '{User.EmailAddress}', '{User.DisplayName}', '{User.Id}'];
export const availableAttributeDisplayName = availableAttributes[0];

export enum CLAIM_PROPERTIES {
    Format = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claimproperties/format',
    DisplayName = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claimproperties/displayname',
    AttributeNameFormat = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claimproperties/attributename',
    NameQualifier = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claimproperties/namequalifier',
    SPNameQualifier = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claimproperties/spnamequalifier',
    SPProvidedID = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claimproperties/spprovidedid'
}

// CERTIFICATES
export enum CERTIFICATE_TYPE {
    signing = 'Signing',
    encryption = 'Encryption'
}

export enum CertificateComponentLables {
    Subject = 'CERTIFICATE_SUBJECT',
    Issuer = 'CERTIFICATE_ISSUER',
    Date = 'CERTIFICATE_EXPIRY',
    Thumbprint = 'CERTIFICATE_THUMBPRINT'
}

export const DEEP_LINKS: IDeepLink[] = [
    {
        Enabled: true,
        DisplayName: 'MSOL_EXCEL_WEB',
        IconUrl: '/assets/images/apps/excel.png',
        Url: 'https://login.microsoftonline.com/login.srf?wa=wsignin1.0&wreply=https%3A%2F%2Foffice.live.com%2Fstart%2FExcel.aspx%3Fauth%3D2&whr=${domain}',
        linkClass: ''
    },
    {
        Enabled: true,
        DisplayName: 'MSOL_ONEDRIVE_WEB',
        IconUrl: '/assets/images/apps/onedrive.png',
        Url: 'https://login.microsoftonline.com/login.srf?wa=wsignin1.0&wreply=https%3A%2F%2F${managedomain}-my.sharepoint.com%2F&whr=${domain}',
        linkClass: ''
    },
    {
        Enabled: true,
        DisplayName: 'MSOL_CALENDAR_WEB',
        IconUrl: '/assets/images/apps/calendar.png',
        Url: 'https://outlook.office.com/owa/?realm=${domain}&path=/calendar/view/Month',
        linkClass: ''
    },
    { Enabled: true, DisplayName: 'MSOL_MAIL_WEB', IconUrl: '/assets/images/apps/outlook.png', Url: 'https://outlook.com/${domain}', linkClass: '' },
    {
        Enabled: true,
        DisplayName: 'MSOL_SHAREPOINT_WEB',
        IconUrl: '/assets/images/apps/sharepoint.png',
        Url: 'https://login.microsoftonline.com/login.srf?wa=wsignin1.0&wreply=https%3A%2F%2F${managedomain}.sharepoint.com%2F&whr=${domain}',
        linkClass: ''
    },
    {
        Enabled: true,
        DisplayName: 'MSOL_WORD_WEB',
        IconUrl: '/assets/images/apps/word.png',
        Url: 'https://login.microsoftonline.com/login.srf?wa=wsignin1.0&wreply=https%3A%2F%2Foffice.live.com%2Fstart%2FWord.aspx%3Fauth%3D2&whr=${domain}',
        linkClass: ''
    },
    {
        Enabled: true,
        DisplayName: 'MSOL_AZURE_PORTAL_WEB',
        IconUrl: '/assets/images/apps/azure.png',
        Url: 'https://login.microsoftonline.com/login.srf?wa=wsignin1%2E0&rver=6%2E1%2E6206%2E0&wreply=https%3A%2F%2Fportal.azure.com%2Fsignin%2Findex%2F&whr=${domain}',
        linkClass: ''
    }
];

export enum MODULE_NAME {
    msolModule = 'msol-module',
    graphModule = 'graph-module'
}

export enum Fields {
    domain,
    username,
    password
}

export const SYNC_SOURCES_BACKER: ISyncSource[] = [
    { label: 'Email Address', value: 'EmailAddress' },
    { label: 'Principal Name', value: 'PrincipalName' },
    { label: 'Principal Name with Organization Suffix', value: 'PrincipalNameWithSuffix', disabled: true }
];

export const MEMBERSHIP_SYNC_MODES: ISyncSource[] = [
    { label: 'Add Members only', value: 'AddMembersOnly' },
    { label: 'Add and Remove Members', value: 'AddRemoveMembers' },
];

export const GROUP_TYPE_SYNC_MODES_BACKER: IGroupTypeSyncMode[] = [
    { label: 'Only if Type and Email match', value: 'IfTypeMatches' },
    { label: 'Regardless of Type and Email', value: 'RegardlessOfType' }
];
export const HELP_MENU: IHelpMenuItem[] = [
    { url: '', label: 'HELP_MENU_HELP', type: 'help-text' },
    { url: 'https://helpdesk.kaseya.com/hc/en-gb', label: 'HELP_MENU_KNOWLEDGE_BASE', type: 'item' },
    { url: 'https://www.kaseya.com/customer-success/training/', label: 'HELP_MENU_KASEYA_UNIVERSITY', type: 'item' },
    { url: 'https://status.kaseya.net/pages/5a317d8a2e604604d65c1c76', label: 'STATUS', type: 'item' },
    {
        url: 'https://community.kaseya.com/',
        label: 'HELP_MENU_SUGGEST_A_FEATURE',
        type: 'item'
    },
    { url: 'https://helpdesk.kaseya.com/hc/en-gb/sections/4406076840721-Release-Notes', label: 'HELP_MENU_RELEASE_NOTES', type: 'item' },
    { url: '/apps/videos', label: 'HELP_MENU_IN_APP_VIDEOS', type: 'in-app-videos' },
    { url: '', label: 'HELP_MENU_LEGAL', type: 'help-text' },
    { url: 'https://www.kaseya.com/legal/kaseya-master-agreement/', label: 'HELP_MENU_TERMS_OF_USE', type: 'item' },
    { url: 'https://www.idagent.com/privacy-policy/', label: 'HELP_MENU_PRIVACY_POLICY', type: 'item' },
    { url: '', label: '', type: 'divider' }
];
